import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import SelectCst from "@components/InputCst/SelectCst";
import { SelectedTargetItem } from "../../types";
import {
  GroupListItem,
  GroupType,
  useGroupsList,
} from "../../hooks/useGroupsList";
import GroupRow from "./rows/GroupRow";

const PAGE_SIZE = 10;

interface GroupsTabProps {
  corporateId: string | number;
  selected: SelectedTargetItem[];
  onChange: (next: SelectedTargetItem[]) => void;
}

const GroupsTab: React.FC<GroupsTabProps> = ({
  corporateId,
  selected,
  onChange,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [groupType, setGroupType] = useState<GroupType | "">("");

  const { items, totalItems, totalPages, isLoading } = useGroupsList({
    corporateId,
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    groupType: groupType || undefined,
  });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => s.id)),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((g) => String(g.id) === String(id));
      if (!found) return;
      onChange([
        ...selected,
        {
          id: found.id,
          label: found.name || `Gruppo ${found.id}`,
          groupType: found.type,
          meta: found,
        },
      ]);
    } else {
      onChange(selected.filter((s) => String(s.id) !== String(id)));
    }
  };

  const typeOptions = [
    { label: t("targets.groups.type.all") || "Tutti", value: "" },
    { label: t("targets.groups.type.static") || "Statico", value: "STATIC" },
    { label: t("targets.groups.type.dynamic") || "Dinamico", value: "DYNAMIC" },
  ];

  const typeFilter = (
    <Box
      sx={{
        width: 200,
        flexShrink: 0,
        "& .MuiFormControl-root": {
          minWidth: "0 !important",
          width: "100% !important",
        },
        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
        "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
      }}
    >
      <SelectCst
        id="enrollment-group-type"
        label={t("targets.groups.type.label") || "Tipologia"}
        value={groupType}
        options={typeOptions}
        onChange={(v: any) => {
          setGroupType((v as GroupType) || "");
          setPage(1);
        }}
      />
    </Box>
  );

  return (
    <SearchableMultiSelectListCst<GroupListItem>
      items={items}
      getId={(g) => g.id}
      renderRow={({ item }) => <GroupRow group={item} />}
      selectedIds={selectedSet}
      onToggle={handleToggle}
      initialSearchValue={search}
      onSearchSubmit={(v) => {
        setSearch(v);
        setPage(1);
      }}
      searchPlaceholder={t("targets.groups.search-placeholder") || "Nome"}
      searchButtonLabel={t("targets.search-button") || "Cerca"}
      filtersSlot={typeFilter}
      page={page}
      pageSize={PAGE_SIZE}
      totalItems={totalItems}
      totalPages={totalPages}
      onPageChange={setPage}
      isLoading={isLoading}
      selectedCountLabel={
        t("targets.selected-count", { count: selected.length }) ||
        `${selected.length} elementi selezionati`
      }
      emptyLabel={t("targets.groups.empty") || "Nessun gruppo trovato"}
    />
  );
};

export default GroupsTab;
